import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Paper, alpha } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Responsive, verticalCompactor } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import WidgetRenderer from "./widgets/WidgetRenderer";
import { dashTileSx, STUDIO_ACCENT } from "./myDashboardStyles";
import {
  DASH_BREAKPOINTS,
  DASH_COLS,
  ensureResponsiveLayouts,
} from "./responsiveLayouts";

export default function DashboardCanvas({
  widgets,
  layouts,
  editing,
  onLayoutChange,
  onRemove,
}) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  const responsiveLayouts = useMemo(
    () => ensureResponsiveLayouts(widgets, layouts),
    [widgets, layouts],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const measure = () => {
      const next = Math.floor(el.getBoundingClientRect().width);
      if (next > 0) setWidth(next);
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const next = Math.floor(el.getBoundingClientRect().width);
    if (next > 0) setWidth(next);
  }, [editing, widgets.length]);

  const isNarrow = width > 0 && width < DASH_BREAKPOINTS.sm;
  const rowHeight = isNarrow ? 72 : 88;
  const margin = isNarrow ? [10, 10] : [16, 16];

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minHeight: 320,
        boxSizing: "border-box",
        "& .react-grid-layout": {
          width: "100% !important",
        },
        "& .react-grid-item": {
          transition: editing
            ? "transform 0.2s ease, width 0.2s ease, height 0.2s ease"
            : "none",
        },
        "& .react-grid-placeholder": {
          background: alpha(STUDIO_ACCENT, 0.14),
          borderRadius: "14px",
          border: `1px dashed ${alpha(STUDIO_ACCENT, 0.45)}`,
        },
        "& .react-resizable-handle::after": {
          borderColor: STUDIO_ACCENT,
          width: 8,
          height: 8,
        },
      }}
    >
      {width > 0 && (
        <Responsive
          className="layout"
          width={width}
          layouts={responsiveLayouts}
          breakpoints={DASH_BREAKPOINTS}
          cols={DASH_COLS}
          rowHeight={rowHeight}
          margin={margin}
          dragConfig={{
            enabled: editing,
            handle: ".mydash-card",
            cancel: "button, a, .MuiButton-root, .MuiIconButton-root, input",
          }}
          resizeConfig={{ enabled: editing && !isNarrow }}
          compactor={verticalCompactor}
          onLayoutChange={(_current, allLayouts) => {
            if (!editing) return;
            // En móvil/tablet estrecha no pisamos el layout de escritorio (lg).
            if (width >= DASH_BREAKPOINTS.md && allLayouts?.lg) {
              onLayoutChange(allLayouts.lg);
            }
          }}
        >
          {widgets.map((widget) => (
            <div key={widget.i}>
              <Paper
                elevation={0}
                className="mydash-card"
                sx={(theme) => ({
                  ...dashTileSx(theme, { editing }),
                  cursor: editing ? "grab" : "default",
                  "&:active": editing ? { cursor: "grabbing" } : undefined,
                })}
              >
                {editing && (
                  <>
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        left: 10,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        color: "text.disabled",
                        pointerEvents: "none",
                      }}
                    >
                      <DragIndicatorIcon sx={{ fontSize: 18, opacity: 0.5 }} />
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(widget.i);
                      }}
                      sx={(theme) => ({
                        position: "absolute",
                        top: 4,
                        right: 4,
                        zIndex: 3,
                        width: 28,
                        height: 28,
                        backgroundColor: alpha(
                          theme.palette.background.paper,
                          0.92,
                        ),
                        border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                          color: theme.palette.error.main,
                        },
                      })}
                      aria-label="remove"
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </>
                )}
                <Box
                  sx={{
                    height: "100%",
                    position: "relative",
                    zIndex: 1,
                    pt: editing ? 2.5 : 0,
                    pointerEvents: editing ? "none" : "auto",
                    "& .MuiButton-root": {
                      pointerEvents: "auto",
                    },
                  }}
                >
                  <WidgetRenderer widget={widget} editing={editing} />
                </Box>
              </Paper>
            </div>
          ))}
        </Responsive>
      )}
    </Box>
  );
}
